/**
 * DtcGrowthTrends - Revenue chart for DTC platforms
 * 
 * Shows how each DTC platform contributes to total revenue over time
 * Mirrors RetailerGrowthTrends but for DTC data (daily instead of weekly)
 */

import React, { useState } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { BarChart2, TrendingUp } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';

interface DtcTrendDataPoint {
  date: string;
  total_revenue: number;
  total_orders: number;
  avg_order_value: number;
  total_units: number;
}

interface DtcPlatformBreakdown {
  platform_id: string;
  platform_name: string;
  platform_type: string;
  revenue: number;
  orders: number;
}

interface DtcGrowthTrendsProps {
  startDate: string;
  endDate: string;
}

// Platform brand colors (matching DtcPlatformCard)
const PLATFORM_COLORS: Record<string, string> = {
  shopify: '#95BF47',
  dtc_shopify: '#95BF47',
  amazon: '#FF9900',
  dtc_amazon: '#FF9900',
  amazon_seller_central: '#FF9900',
  walmart: '#0071CE',
  dtc_walmart: '#0071CE'
};

export function DtcGrowthTrends({ startDate, endDate }: DtcGrowthTrendsProps) {
  const [chartType, setChartType] = useState<'stacked' | 'lines'>('stacked');

  // Fetch trend data with per-platform breakdown
  const { data: dtcTrendData, isLoading } = useQuery({
    queryKey: ['dtc-trends', startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('start_date', startDate);
      params.append('end_date', endDate);
      console.log('[DtcGrowthTrends] Fetching trends:', { startDate, endDate });
      const response = await apiClient.get(`/dtc/trends?${params.toString()}`);
      return response;
    },
    enabled: !!startDate && !!endDate,
  });

  if (isLoading) {
    return (
      <div className="bg-white border border-purple-200 rounded-xl p-8">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-64 mb-2"></div>
          <div className="h-4 bg-gray-200 rounded w-48 mb-4"></div>
          <div className="h-80 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  // Defensive check: ensure data exists before accessing properties
  if (!dtcTrendData || !dtcTrendData.daily_trends || dtcTrendData.daily_trends.length === 0) {
    return (
      <div className="bg-white border border-purple-200 rounded-xl p-8 text-center">
        <p className="text-gray-500">Not enough historical data to show trends</p>
      </div>
    );
  }

  const trendData = dtcTrendData.daily_trends;
  const platforms = Array.isArray(dtcTrendData.platforms) ? dtcTrendData.platforms : [];

  console.log('[DtcGrowthTrends] ===== FULL DATA DUMP =====');
  console.log('[DtcGrowthTrends] Raw API response:', JSON.stringify(dtcTrendData, null, 2));
  console.log('[DtcGrowthTrends] Platforms:', JSON.stringify(platforms, null, 2));
  console.log('[DtcGrowthTrends] Sample day 0:', JSON.stringify(trendData[0], null, 2));
  console.log('[DtcGrowthTrends] Sample day by_platform:', trendData[0]?.by_platform);

  const formatDate = (dateStr: string) => {
    // Handle both date formats: "2025-11-07" and "2025-11-07T00:00:00.000Z"
    // CRITICAL: Always use 'Z' suffix and timeZone: 'UTC' to prevent date shift
    const formattedDateStr = dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00Z';
    const date = new Date(formattedDateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      notation: 'compact',
      compactDisplay: 'short'
    }).format(value);
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum: number, entry: any) => sum + (entry.value || 0), 0);
      
      return (
        <div className="bg-white border border-purple-200 rounded-lg shadow-lg p-3 min-w-[200px]">
          <p className="text-sm font-semibold text-gray-900 mb-2">{formatDate(label)}</p>
          <div className="space-y-1">
            {payload.reverse().map((entry: any, index: number) => {
              const percentage = total > 0 ? (entry.value / total) * 100 : 0;
              return (
                <div key={index} className="flex items-center justify-between space-x-3 text-xs">
                  <div className="flex items-center space-x-1.5">
                    <div 
                      className="w-2 h-2 rounded-full" 
                      style={{ backgroundColor: entry.color }}
                    />
                    <span className="font-medium text-gray-700">
                      {entry.name}:
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900">
                      {formatCurrency(entry.value)}
                    </div>
                    <div className="text-gray-500">
                      {percentage.toFixed(0)}%
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="border-t border-gray-200 mt-2 pt-2 flex items-center justify-between font-semibold">
              <span className="text-gray-700">Total:</span>
              <span className="text-gray-900">{formatCurrency(total)}</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Transform data for chart with per-platform breakdown
  const chartData = Array.isArray(trendData) ? trendData.map((day: any) => {
    const dataPoint: any = {
      date: day.date
    };
    
    // Initialize all platforms with 0 to ensure they show in chart
    if (Array.isArray(platforms)) {
      platforms.forEach((platform: any) => {
        if (platform && platform.platform_name) {
          dataPoint[platform.platform_name] = 0;
        }
      });
    }
    
    // Add actual revenue for platforms that have data
    if (day.by_platform && Array.isArray(day.by_platform)) {
      day.by_platform.forEach((platform: any) => {
        if (platform && platform.platform_name) {
          dataPoint[platform.platform_name] = platform.revenue || 0;
        }
      });
    }
    
    return dataPoint;
  }) : [];

  console.log('[DtcGrowthTrends] Transformed chart data sample:', JSON.stringify(chartData[0], null, 2));
  console.log('[DtcGrowthTrends] All chart data:', JSON.stringify(chartData, null, 2));

  return (
    <div className="bg-white border border-purple-200 rounded-xl p-6 shadow-sm">
      {/* Header with Chart Type Toggle */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">DTC Revenue Trend (Last {trendData.length} Days)</h3>
          <p className="text-sm text-gray-600">Daily revenue performance by platform</p>
        </div>
        
        {/* Chart Type Toggle */}
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setChartType('stacked')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
              chartType === 'stacked'
                ? 'bg-purple-600 text-white border-purple-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <BarChart2 className="w-4 h-4 inline-block mr-1" />
            Stacked
          </button>
          <button
            onClick={() => setChartType('lines')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg border transition-colors ${
              chartType === 'lines'
                ? 'bg-purple-600 text-white border-purple-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <TrendingUp className="w-4 h-4 inline-block mr-1" />
            Lines
          </button>
        </div>
      </div>

      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'stacked' ? (
            <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis 
                dataKey="date" 
                tickFormatter={formatDate}
                stroke="#6B7280"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                tickFormatter={(value) => formatCurrency(value)}
                stroke="#6B7280"
                style={{ fontSize: '12px' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
                iconType="rect"
              />
              {/* Render a bar for each platform */}
              {Array.isArray(platforms) && platforms.map((platform: any) => {
                if (!platform || !platform.platform_name) return null;
                const platformType = platform.platform_type || 'shopify';
                const color = PLATFORM_COLORS[platformType] || '#a855f7';
                return (
                  <Bar
                    key={platform.platform_id}
                    dataKey={platform.platform_name}
                    stackId="revenue"
                    fill={color}
                    name={platform.platform_name}
                  />
                );
              })}
            </BarChart>
          ) : (
            <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis 
                dataKey="date" 
                tickFormatter={formatDate}
                stroke="#6B7280"
                style={{ fontSize: '12px' }}
              />
              <YAxis 
                tickFormatter={(value) => formatCurrency(value)}
                stroke="#6B7280"
                style={{ fontSize: '12px' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ fontSize: '12px' }}
                iconType="line"
              />
              {/* Render a line for each platform */}
              {Array.isArray(platforms) && platforms.map((platform: any) => {
                if (!platform || !platform.platform_name) return null;
                const platformType = platform.platform_type || 'shopify';
                const color = PLATFORM_COLORS[platformType] || '#a855f7';
                return (
                  <Line
                    key={platform.platform_id}
                    type="monotone"
                    dataKey={platform.platform_name}
                    stroke={color}
                    strokeWidth={3}
                    dot={{ r: 5, fill: color }}
                    activeDot={{ r: 7 }}
                    name={platform.platform_name}
                  />
                );
              })}
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>

      {/* Legend Helper */}
      <div className="mt-4 flex items-center justify-center space-x-4 text-xs text-gray-500">
        <div className="flex items-center space-x-1">
          <BarChart2 className="w-3 h-3" />
          <span>Stacked bars show platform contribution</span>
        </div>
        <span>•</span>
        <div className="flex items-center space-x-1">
          <TrendingUp className="w-3 h-3" />
          <span>Lines view shows individual platform trends</span>
        </div>
      </div>
    </div>
  );
}

